import { validClientAttempt, safeMarket, type ClientAttemptEvent } from '../../../relay/src/diagnostics/polyAttempt';

const KEY = 'g1.poly-attempts.v1';
const MAX = 96;
const AGE = 86_400_000;
const UPLOAD_INTERVAL = 10_000;
const FAILURE_BACKOFF = 60_000;
const MAX_BACKOFF = 300_000;
type Send = (events: ClientAttemptEvent[], signal: AbortSignal) => Promise<boolean>;
let owner: string | null = null, epoch = 0;
let ring: ClientAttemptEvent[] = [], pending: ClientAttemptEvent[] = [];
let send: Send | undefined, request: AbortController | undefined;
let storageTimer: ReturnType<typeof setTimeout> | undefined;
let uploadTimer: ReturnType<typeof setTimeout> | undefined;
let nextUploadAt = 0, failures = 0;
const listeners = new Set<() => void>();
const notify = () => { for (const f of listeners) { try { f(); } catch { /* Diagnostics stay optional. */ } } };
export const subscribePolyAttempts = (f: () => void) => { listeners.add(f); return () => { listeners.delete(f); }; };
export const polyAttemptCount = () => ring.filter(e => Date.now() - e.at < AGE).length;

function persistLater() {
  if (storageTimer !== undefined) return;
  storageTimer = setTimeout(() => {
    storageTimer = undefined;
    try { if (owner) sessionStorage.setItem(KEY, JSON.stringify({ owner, savedAt: Date.now(), events: ring,
      pendingIds: pending.map(e => e.eventId),
      retryAfter: Date.now() + Math.max(0, nextUploadAt - performance.now()), failures })); }
    catch { /* Memory/download remain available when tab storage is disabled. */ }
  }, 0);
}
function queueUpload() {
  if (uploadTimer !== undefined || request || !owner || !send || pending.length === 0) return;
  // Never awaited by placement, never refreshes auth, never retries an order.
  uploadTimer = setTimeout(() => { uploadTimer = undefined; void flush(); },
    Math.max(UPLOAD_INTERVAL, nextUploadAt - performance.now()));
}
async function flush() {
  if (request || !owner || !send || pending.length === 0) return;
  const generation = epoch, transport = send;
  const batch = pending.filter(e => Date.now() - e.at < AGE).slice(0, 32);
  if (!batch.length) { pending = []; return; }
  const controller = new AbortController(); request = controller;
  nextUploadAt = performance.now() + UPLOAD_INTERVAL;
  const timeout = setTimeout(() => controller.abort(), 5_000);
  let delivered = false;
  try { delivered = await transport(batch, controller.signal); }
  catch { /* Keep a bounded local receipt; new events/online may retry diagnostics only. */ }
  finally {
    clearTimeout(timeout);
    if (request === controller) request = undefined;
    if (generation === epoch) {
      if (delivered) {
        failures = 0;
        const ids = new Set(batch.map(e => e.eventId)); pending = pending.filter(e => !ids.has(e.eventId));
        queueUpload();
      } else {
        failures = Math.min(failures + 1, 4);
        nextUploadAt = performance.now() + Math.min(MAX_BACKOFF, FAILURE_BACKOFF * 2 ** (failures - 1));
        // No autonomous retry loop. A later event/online trigger observes the
        // backoff; retained local evidence remains downloadable throughout.
      }
      persistLater();
    }
  }
}
export function setPolyAttemptSession(next: string | null, transport?: Send) {
  send = transport;
  if (owner === next) return;
  epoch++; request?.abort(); request = undefined;
  clearTimeout(storageTimer); storageTimer = undefined;
  clearTimeout(uploadTimer); uploadTimer = undefined;
  const previous = owner; owner = next; ring = []; pending = []; failures = 0; nextUploadAt = 0;
  try {
    const raw = sessionStorage.getItem(KEY), saved = raw && raw.length < 64_000 ? JSON.parse(raw) : null;
    if (!previous && next && saved?.owner === next && Date.now() - saved.savedAt >= 0
      && Date.now() - saved.savedAt < AGE && Array.isArray(saved.events) && saved.events.length <= MAX
      && saved.events.every(validClientAttempt)) {
      ring = saved.events.filter((e: ClientAttemptEvent) => e.at <= Date.now() && Date.now() - e.at < AGE);
      // Old journals have no delivery ledger: retain them locally, but never
      // replay all previously delivered evidence on every reload.
      const ids = new Set(Array.isArray(saved.pendingIds) ? saved.pendingIds.slice(0, MAX) : []);
      pending = ring.filter(e => ids.has(e.eventId));
      failures = Number.isInteger(saved.failures) ? Math.max(0, Math.min(4, saved.failures)) : 0;
      nextUploadAt = performance.now() + (Number.isFinite(saved.retryAfter)
        ? Math.max(0, Math.min(MAX_BACKOFF, saved.retryAfter - Date.now())) : 0);
      queueUpload();
    } else sessionStorage.removeItem(KEY);
  } catch { /* Invalid saved data is never exported/uploaded. */ }
  notify();
}
export function clearPolyAttempts() {
  epoch++; request?.abort(); request = undefined;
  clearTimeout(storageTimer); storageTimer = undefined;
  clearTimeout(uploadTimer); uploadTimer = undefined;
  ring = []; pending = []; failures = 0; nextUploadAt = 0;
  try { sessionStorage.removeItem(KEY); } catch { /* Optional storage. */ }
  notify();
}
export function exportPolyAttempts() {
  return JSON.stringify({ version: 1, source: 'browser_report', events: ring.filter(e => Date.now() - e.at < AGE) }, null, 2);
}
export type PolyAttempt = ReturnType<typeof beginPolyAttempt>;
export function beginPolyAttempt(market: unknown) {
  const generation = epoch, scope = owner;
  let attemptId: string;
  try { attemptId = crypto.randomUUID(); } catch { return null; }
  let stage: ClientAttemptEvent['stage'] = 'intent';
  const record = (next: ClientAttemptEvent['stage'], status: number | null = null) => {
    stage = next;
    if (!scope || scope !== owner || generation !== epoch) return;
    try {
      const event: ClientAttemptEvent = { eventId: crypto.randomUUID(), attemptId, at: Date.now(),
        market: safeMarket(market), stage: next, status };
      ring = [...ring.filter(e => event.at - e.at < AGE), event].slice(-MAX);
      pending = [...pending, event].slice(-MAX);
      persistLater(); notify();
      if (next !== 'intent' && next !== 'dispatched') queueUpload();
    } catch { /* Diagnostic failure does not affect a deliberate order. */ }
  };
  record('intent');
  return { id: attemptId, dispatched: () => record('dispatched'),
    response: (status: number) => record('response', status), unknown: () => record('unknown'),
    blocked: () => record('blocked'), finish: () => { if (stage === 'intent') record('blocked'); } };
}
if (typeof window !== 'undefined') window.addEventListener('online', queueUpload);
